import IconButton from '@/components/Buttons/IconButton';
import MultiSelect from '@/components/Selects/MultiSelect';
import { styles, UpgradeNeededTooltipContent } from '@/components/Voice/VoiceModal/UpgradeNeededTooltipContent';
import { useTranslationVoicesLanguages } from '@/hooks/useTranslationVoicesLanguages';
import { IVoiceLanguage } from '@/hooks/useVoicesLanguages';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useMemo } from 'react';

const Img = styled.img`
    width: 26px;
    height: 16px;
`;

const OptionLabelWrapper = styled.div`
    display: flex;
    gap: 5px;
`;

function renderOption(option: IVoiceLanguage) {
    return (
        <OptionLabelWrapper className="translation-select__option__label">
            {option.flagSrc ? <Img loading="lazy" src={option.flagSrc} alt={option.label} /> : null}
            {option.label}
        </OptionLabelWrapper>
    );
}

const ChipWrapper = styled.div`
    display: flex;
    align-items: center;
    border: 1px solid #e5e5e5;
    border-radius: 50px;
    padding: 8px 8px 8px 16px;
    height: 36px;
    box-sizing: border-box;
    gap: 6px;
    cursor: default;
`;

const Loader = styled.div`
    font-weight: 700;
`;

const AdornmentBox = styled.div`
    display: flex;
    align-items: center;
`;

const ChipText = styled.div``;

function renderInputChip(option: IVoiceLanguage, onRemove: (option: string) => void) {
    return (
        <ChipWrapper key={option.label}>
            <AdornmentBox>{option?.flagSrc ? <Img loading="lazy" src={option.flagSrc} alt={option.label} /> : null}</AdornmentBox>
            <ChipText>{option.label}</ChipText>
            <IconButton ariaLabel="close" onClick={() => onRemove(option.label)}>
                <CloseIcon />
            </IconButton>
        </ChipWrapper>
    );
}

interface Props {
    className?: string;
    disabled?: boolean;
    useDisabledOptions?: boolean;
    selectedLanguages: string[];
    setSelectedLanguages: (languages: string[]) => void;
}

export const LanguageMultiSelect = ({ className, disabled, useDisabledOptions, selectedLanguages, setSelectedLanguages }: Props) => {
    const { languagesMap: languages, isFetching } = useTranslationVoicesLanguages();

    const removeValue = useCallback(
        (value: string) => setSelectedLanguages(selectedLanguages.filter(v => v !== value)),
        [selectedLanguages]
    );

    const renderChip = useCallback(
        (option: string) => {
            const lang = languages.get(option);

            if (!lang) return null;

            return renderInputChip(lang, removeValue);
        },
        [removeValue]
    );

    const renderOptionValue = useCallback(
        (option: string) => {
            const lang = languages.get(option);

            if (!lang) return null;

            return renderOption(lang);
        },
        [languages]
    );

    const languagesLabels = useMemo(() => Array.from(languages.keys()), [languages]);
    const onClearAll = useCallback(() => setSelectedLanguages([]), [setSelectedLanguages]);

    return (
        <MultiSelect
            id="language-select"
            testId="translations-modal-language-select"
            label="Language"
            disabled={disabled}
            useDisabledOptions={useDisabledOptions}
            placeholder="Select Languages"
            options={languagesLabels}
            values={selectedLanguages}
            noOptionsText={isFetching ? <Loader>Loading...</Loader> : undefined}
            onChange={(_, value) => {
                setSelectedLanguages(value ?? []);
            }}
            renderOptionValue={renderOptionValue}
            renderChip={renderChip}
            className={className}
            disabledItemTooltipStyles={styles}
            disabledItemTooltipContent={
                <UpgradeNeededTooltipContent
                    title="Want to translate more than 3 languages?"
                    text="Unlock bulk translations on our paid plans"
                />
            }
            onClearAll={onClearAll}
            getOptionLabel={option => option}
        />
    );
};
